import { z } from 'zod';

// =====================================================
// Core Enums as Zod Schemas
// =====================================================

export const feedbackTypeSchema = z.enum(['bug', 'enhancement', 'question']);
export const feedbackStatusSchema = z.enum([
  'submitted',
  'triaged',
  'planned',
  'in_progress',
  'resolved',
  'closed',
  'wont_fix',
  'duplicate'
]);
export const feedbackPrioritySchema = z.enum(['low', 'medium', 'high', 'critical']);
export const feedbackSeveritySchema = z.enum(['critical', 'major', 'minor', 'trivial']);
export const feedbackCategorySchema = z.enum([
  'rate-quotes',
  'pre-approvals',
  'contacts',
  'documents',
  'pipeline',
  'admin',
  'encompass',
  'ai-features',
  'other'
]);

// =====================================================
// Bug Report Form Schema
// =====================================================

export const bugReportFormSchema = z.object({
  title: z
    .string()
    .min(1, 'Title is required')
    .max(200, 'Title must be less than 200 characters')
    .trim(),

  type: z.literal('bug'),

  category: feedbackCategorySchema,

  severity: feedbackSeveritySchema,

  description: z
    .string()
    .min(10, 'Please provide a detailed description (at least 10 characters)')
    .max(5000, 'Description must be less than 5000 characters'),

  steps_to_reproduce: z
    .string()
    .max(5000, 'Steps to reproduce must be less than 5000 characters')
    .optional()
    .or(z.literal('')),

  expected_behavior: z
    .string()
    .max(2000, 'Expected behavior must be less than 2000 characters')
    .optional()
    .or(z.literal('')),

  actual_behavior: z
    .string()
    .max(2000, 'Actual behavior must be less than 2000 characters')
    .optional()
    .or(z.literal(''))
});

export type BugReportFormValues = z.infer<typeof bugReportFormSchema>;

export const defaultBugReportValues: BugReportFormValues = {
  title: '',
  type: 'bug',
  category: 'other',
  severity: 'minor',
  description: '',
  steps_to_reproduce: '',
  expected_behavior: '',
  actual_behavior: ''
};

// =====================================================
// Enhancement Request Form Schema
// =====================================================

export const enhancementFormSchema = z.object({
  title: z
    .string()
    .min(1, 'Title is required')
    .max(200, 'Title must be less than 200 characters')
    .trim(),

  type: z.literal('enhancement'),

  category: feedbackCategorySchema,

  description: z
    .string()
    .min(10, 'Please provide a detailed description (at least 10 characters)')
    .max(5000, 'Description must be less than 5000 characters'),

  use_case: z
    .string()
    .max(2000, 'Use case must be less than 2000 characters')
    .optional()
    .or(z.literal(''))
});

export type EnhancementFormValues = z.infer<typeof enhancementFormSchema>;

export const defaultEnhancementValues: EnhancementFormValues = {
  title: '',
  type: 'enhancement',
  category: 'other',
  description: '',
  use_case: ''
};

// =====================================================
// Question Form Schema
// =====================================================

export const questionFormSchema = z.object({
  title: z
    .string()
    .min(1, 'Title is required')
    .max(200, 'Title must be less than 200 characters')
    .trim(),

  type: z.literal('question'),

  category: feedbackCategorySchema,

  description: z
    .string()
    .min(10, 'Please provide details about your question (at least 10 characters)')
    .max(5000, 'Description must be less than 5000 characters')
});

export type QuestionFormValues = z.infer<typeof questionFormSchema>;

export const defaultQuestionValues: QuestionFormValues = {
  title: '',
  type: 'question',
  category: 'other',
  description: ''
};

// =====================================================
// Union Schema for All Feedback Types
// =====================================================

export const feedbackFormSchema = z.discriminatedUnion('type', [
  bugReportFormSchema,
  enhancementFormSchema,
  questionFormSchema
]);

export type FeedbackFormValues = z.infer<typeof feedbackFormSchema>;

// =====================================================
// Admin Edit Form Schema
// =====================================================

export const feedbackEditFormSchema = z.object({
  title: z
    .string()
    .min(1, 'Title is required')
    .max(200, 'Title must be less than 200 characters')
    .trim(),

  description: z
    .string()
    .min(1, 'Description is required')
    .max(5000, 'Description must be less than 5000 characters'),

  category: feedbackCategorySchema,

  status: feedbackStatusSchema,

  priority: feedbackPrioritySchema.optional().or(z.literal('')),

  severity: feedbackSeveritySchema.optional().or(z.literal('')),

  assigned_to: z.string().uuid().optional().or(z.literal('')),

  resolution: z
    .string()
    .max(2000, 'Resolution must be less than 2000 characters')
    .optional()
    .or(z.literal('')),

  duplicate_of: z.string().uuid().optional().or(z.literal(''))
});

export type FeedbackEditFormValues = z.infer<typeof feedbackEditFormSchema>;

// =====================================================
// Comment Form Schema
// =====================================================

export const commentFormSchema = z.object({
  content: z
    .string()
    .min(1, 'Comment cannot be empty')
    .max(2000, 'Comment must be less than 2000 characters')
    .trim(),

  is_internal: z.boolean().default(false)
});

export type CommentFormValues = z.infer<typeof commentFormSchema>;

export const defaultCommentValues: CommentFormValues = {
  content: '',
  is_internal: false
};

// =====================================================
// Filter Form Schema
// =====================================================

export const feedbackFilterSchema = z.object({
  status: z.array(feedbackStatusSchema).optional(),
  type: z.array(feedbackTypeSchema).optional(),
  priority: z.array(feedbackPrioritySchema).optional(),
  category: z.array(feedbackCategorySchema).optional(),
  assigned_to: z.string().optional(),
  search: z.string().optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional()
});

export type FeedbackFilterValues = z.infer<typeof feedbackFilterSchema>;

export const defaultFilterValues: FeedbackFilterValues = {
  status: [],
  type: [],
  priority: [],
  category: [],
  assigned_to: '',
  search: '',
  date_from: '',
  date_to: ''
};
